import { useState } from 'react';
import { Box, Button, CircularProgress, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { useGetCarcinogenicSrtValidacionesQuery } from '../../../services/carcinogenicApi';
import HallazgosEstablecimiento, {
    ErrorSvcc
} from '../../Presentaciones/components/HallazgosEstablecimiento';
import {
    agruparCorrecciones,
    lugarDeCorreccion,
    necesitaDetalle,
    seCorrigeEnEstablecimiento
} from '../pasoDeCorreccion';
import DetalleCorreccion from './DetalleCorreccion';
import useIrAEditarEstablecimiento from '../hooks/useIrAEditarEstablecimiento';
import LugarCorreccion from './LugarCorreccion';
import { CLAVES_NOMBRE_PASO, TAB_INDEX } from '../pasos';

interface Props {
    idInforme?: number | null;
    /**
     * Lleva al paso de una corrección. Si no se pasa, o el paso está bloqueado
     * (`pasoHabilitado`), la corrección se lista sin el botón.
     */
    onIrAPaso?: (paso: TAB_INDEX) => void;
    pasoHabilitado?: (paso: TAB_INDEX) => boolean;
}

const COLOR = {
    rojo: '#B42318',
    rojoTexto: '#8A1C12',
    rojoFondo: '#FFF7F6',
    rojoBorde: '#F5C2BC',
    rojoCirculo: '#FEE4E2',
    ambarTexto: '#8A4B0A',
    ambarFondo: '#FFFAF0',
    ambarBorde: '#F4D7A6',
    ambarCirculo: '#FDEBC8',
    verdeTexto: '#1E5E24',
    verdeFondo: '#F1F8F1',
    verdeBorde: '#C7E3C9',
    texto: '#3D2320',
    gris: '#6B777C'
};

const circuloIcono = (fondo: string, color: string) => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: fondo,
    color
});

/**
 * Qué le falta al informe para poder presentarse ante la SRT, visible mientras
 * se carga y no recién cuando alguien intenta presentar.
 *
 * Consume `GET /srt/presentaciones/:idInforme/validaciones`, que corre las
 * MISMAS reglas del pre-envío que usa el backoffice — no una copia. Eso es
 * deliberado: `tabsValidationHelpers.ts` ya es un segundo conjunto de reglas
 * (completitud de campos) que no sabe nada del catálogo de errores de la SRT, y
 * un tercero garantizaría que se separen. Acá no hay lógica de validación,
 * sólo presentación.
 *
 * Las reglas de informe completo —cardinalidades, "establecimiento sin
 * sustancias", el período de exposición— no se pueden evaluar al guardar un
 * registro suelto, porque el informe se carga de a partes en seis módulos. Por
 * eso esto es un panel de estado y no un bloqueo de guardado.
 *
 * Con bloqueantes, el panel lista cada corrección en lenguaje claro con dónde se
 * corrige (`lugarDeCorreccion`) y un botón para ir al paso; el detalle por
 * registro afectado y las advertencias quedan en "Ver detalle".
 */
const PanelValidacionSrt = ({ idInforme, onIrAPaso, pasoHabilitado }: Props) => {
    const [expandido, setExpandido] = useState(false);
    const { t } = useTranslation('carcinogenicReport');
    const nombrePaso = (paso: TAB_INDEX) => String(t(CLAVES_NOMBRE_PASO[paso]));
    // "Se corrige en Establecimientos" lleva a editar los datos del establecimiento.
    const irAEditarEstablecimiento = useIrAEditarEstablecimiento();

    const { data, isFetching, isError } = useGetCarcinogenicSrtValidacionesQuery(idInforme, {
        skip: !idInforme
    });

    if (!idInforme) return null;

    // Un fallo de la validación no puede trabar la carga: el usuario sigue
    // trabajando y el panel simplemente no opina.
    if (isError) return null;

    const errores: ErrorSvcc[] = data?.data?.errores ?? [];
    const erroresBloqueantes = errores.filter((e) => e.severidad === 'error');
    const bloqueantes = erroresBloqueantes.length;
    const advertencias = errores.length - bloqueantes;

    if (isFetching && !errores.length) {
        return (
            <Box display="flex" alignItems="center" gap={1} sx={{ px: 2, py: 1.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Revisando el informe…
                </Typography>
            </Box>
        );
    }

    if (!errores.length) {
        return (
            <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: '12px',
                    bgcolor: COLOR.verdeFondo,
                    border: `1px solid ${COLOR.verdeBorde}`,
                    color: COLOR.verdeTexto
                }}
            >
                <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Sin observaciones: el informe está en condiciones de presentarse.
                </Typography>
            </Box>
        );
    }

    const conBloqueantes = bloqueantes > 0;
    const correcciones = agruparCorrecciones(erroresBloqueantes);

    return (
        <Box
            data-testid="panel-validacion-srt"
            sx={{
                borderRadius: '12px',
                border: `1px solid ${conBloqueantes ? COLOR.rojoBorde : COLOR.ambarBorde}`,
                bgcolor: conBloqueantes ? COLOR.rojoFondo : COLOR.ambarFondo,
                p: 2
            }}
        >
            <Box display="flex" alignItems="center" gap={1.5}>
                {conBloqueantes ? (
                    <Box sx={circuloIcono(COLOR.rojoCirculo, COLOR.rojo)}>
                        <ErrorOutlineIcon sx={{ fontSize: 20 }} />
                    </Box>
                ) : (
                    <Box sx={circuloIcono(COLOR.ambarCirculo, COLOR.ambarTexto)}>
                        <WarningAmberIcon sx={{ fontSize: 20 }} />
                    </Box>
                )}

                <Box flex={1} minWidth={0}>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            color: conBloqueantes ? COLOR.rojoTexto : COLOR.ambarTexto
                        }}
                    >
                        {conBloqueantes
                            ? bloqueantes === 1
                                ? '1 corrección antes de enviar'
                                : `${bloqueantes} correcciones antes de enviar`
                            : 'Revisión previa a la presentación'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLOR.gris }}>
                        {conBloqueantes
                            ? 'La SRT rechazaría el informe con estos datos. Corregilos para poder enviarlo.'
                            : 'Nada de esto frena el envío, pero conviene revisarlo.'}
                        {advertencias > 0 &&
                            ` ${advertencias === 1 ? 'Hay 1 advertencia' : `Hay ${advertencias} advertencias`} en el detalle.`}
                    </Typography>
                </Box>

                <Button
                    size="small"
                    onClick={() => setExpandido((previo) => !previo)}
                    endIcon={
                        <ExpandMoreIcon
                            sx={{
                                transform: expandido ? 'rotate(180deg)' : 'none',
                                transition: 'transform .15s'
                            }}
                        />
                    }
                    sx={{
                        flexShrink: 0,
                        borderRadius: '8px',
                        textTransform: 'none',
                        color: COLOR.gris
                    }}
                >
                    {expandido ? 'Ocultar detalle' : 'Ver detalle'}
                </Button>
            </Box>

            {conBloqueantes && (
                <Box
                    component="ul"
                    sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0, display: 'grid', gap: 1 }}
                >
                    {correcciones.map((correccion) => {
                        const lugar = lugarDeCorreccion(correccion, nombrePaso);
                        const conBoton =
                            correccion.paso !== null &&
                            !!onIrAPaso &&
                            (pasoHabilitado ? pasoHabilitado(correccion.paso) : true);
                        return (
                            <Box
                                component="li"
                                key={correccion.codigo}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 1.75,
                                    py: 1.25,
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #F3D9D5',
                                    borderRadius: '10px'
                                }}
                            >
                                <Box flex={1} minWidth={0}>
                                    <Typography variant="body2" sx={{ color: COLOR.texto }}>
                                        {correccion.mensajeUsuario}
                                        {correccion.cantidad > 1 && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    ml: 0.75,
                                                    px: 0.75,
                                                    borderRadius: '8px',
                                                    bgcolor: COLOR.rojoCirculo,
                                                    color: COLOR.rojo,
                                                    fontSize: 12,
                                                    fontWeight: 600
                                                }}
                                            >
                                                ×{correccion.cantidad}
                                            </Box>
                                        )}
                                    </Typography>
                                    {necesitaDetalle(correccion.codigo) && (
                                        <DetalleCorreccion
                                            detalles={correccion.detalles}
                                            comoResolver={correccion.comoResolver}
                                        />
                                    )}
                                    {lugar && (
                                        <div>
                                            <LugarCorreccion
                                                lugar={lugar}
                                                enInforme={correccion.paso !== null}
                                                onClick={
                                                    irAEditarEstablecimiento &&
                                                    seCorrigeEnEstablecimiento(correccion.codigo)
                                                        ? irAEditarEstablecimiento
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    )}
                                </Box>
                                {conBoton && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => onIrAPaso!(correccion.paso!)}
                                        sx={{
                                            flexShrink: 0,
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}
                                    >
                                        Ir al paso
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/*
             * `unmountOnExit` y no sólo ocultar: un informe puede arrastrar
             * decenas de hallazgos y el panel está siempre en pantalla. Cerrado
             * no tiene que costar nada.
             */}
            <Collapse in={expandido} unmountOnExit>
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#FFFFFF', borderRadius: '10px' }}>
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                    >
                        {conBloqueantes
                            ? 'Las correcciones impiden presentar el informe ante la SRT. Las advertencias no frenan el envío.'
                            : 'Ninguna de estas observaciones frena el envío, pero conviene revisarlas.'}
                    </Typography>
                    <HallazgosEstablecimiento errores={errores} />
                </Box>
            </Collapse>
        </Box>
    );
};

export default PanelValidacionSrt;
